<template>
  <div class="min-h-screen bg-background">
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p class="mt-4 text-muted-foreground">{{ $t('common.loadingZones') }}</p>
    </div>

    <div v-else-if="error" class="text-center py-8 text-destructive">
      {{ error }}
    </div>

    <template v-else>
      <CollapsingHeader :title="$t('zones.allZones')">
        <template #icon="{ size }">
          <Compass
            class="text-primary shrink-0"
            :style="{ width: size + 'px', height: size + 'px' }"
          />
        </template>
      </CollapsingHeader>

      <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div v-if="zones.length === 0" class="text-center py-8 text-muted-foreground">
          {{ $t('zones.noZones') }}
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ZoneCard v-for="zone in zones" :key="zone.slug" :zone="zone" />
        </div>
      </main>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Compass } from 'lucide-vue-next'
import { usePageTitle } from '../composables/usePageTitle'
import { useSeoTags } from '../composables/useSeoTags'
import { seoUrl } from '../utils/seoUrl'
import zonesApi from '../api/zones'
import CollapsingHeader from '../components/CollapsingHeader.vue'
import ZoneCard from '../components/ZoneCard.vue'

const { t, locale } = useI18n()
usePageTitle(() => t('titles.zones'))
useSeoTags(() => ({
  title: t('titles.zones'),
  url: seoUrl('Zones', locale.value),
}))
const zones = ref([])
const loading = ref(true)
const error = ref(null)

const fetchZones = async () => {
  try {
    const response = await zonesApi.getAll()
    zones.value = response.data
  } catch (error_) {
    error.value = t('common.errorLoadingZones')
    console.error('Error fetching zones:', error_)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchZones()
})
</script>
